import { log } from '@/logger/logger';
import { orderedVideoConcatenatorInputs } from '@/utils/video-concatenator-utils';
import { HandleType } from '@/enums/handle-type.enum';
import { FlowGraph } from '../FlowGraph';
import { UndoRedoEntry } from '../undoRedo';
import type { VideoConcatenatorData } from '@/types/node';
import type { ImageMode, NodeId, InputId } from 'web';

const logger = log.getLogger('VideoConcatenatorView');

export class VideoConcatenatorView {
  constructor(
    private graph: FlowGraph,
    private nodeId: NodeId,
  ) {}

  /** Sets how each video fits within the output dimensions. */
  setScaleMode(scaleMode: ImageMode) {
    this.graph.wasmEdit((wasm) => wasm.setVideoConcatenatorScaleMode(this.nodeId, scaleMode));
  }

  /**
   * Moves an input to its final zero-based index.
   *
   * @returns `true` if the input was moved; `false` if the request was invalid, the index was
   * unchanged, or the move failed.
   */
  moveInput(inputId: InputId, toIndex: number): boolean {
    const nodeId = this.nodeId;
    const { input } = this.graph.store.getNodeData<VideoConcatenatorData>(nodeId);
    const from = orderedVideoConcatenatorInputs(input).findIndex((entry) => entry.inputId === inputId);
    if (from < 0) {
      logger.error('Input ID not found', { nodeId, inputId, toIndex });
      return false;
    }
    if (!Number.isInteger(toIndex) || toIndex < 0 || toIndex >= input.length) {
      logger.error('Invalid destination index', { nodeId, inputId, toIndex });
      return false;
    }
    if (from === toIndex) return false;
    try {
      this.graph.wasmEdit((wasm) => wasm.moveVideoConcatenatorInput(nodeId, inputId, toIndex));
    } catch (error) {
      logger.error('WASM move failed', { nodeId, inputId, toIndex, error });
      return false;
    }
    this.graph.loadResources();
    return true;
  }

  /** Appends a new `video_N` input handle so more videos can be chained. */
  addNodeInputHandle() {
    this.graph.edit(() => {
      return this.addInputHandleWithKey().undoEntry;
    });
  }

  /** Must be called inside `graph.edit`. */
  addInputHandleWithKey(): { key: string; undoEntry: UndoRedoEntry } {
    const { handles } = this.graph.store.getNodeData<VideoConcatenatorData>(this.nodeId);
    const order = Object.keys(handles.input).length;
    const newInputKey = `video_${order + 1}`;

    const undoEntry = this.graph.wasmChange((wasm) => wasm.addInputHandle(this.nodeId, newInputKey));

    undoEntry.add(
      this.graph.addNodeInputHandle(this.nodeId, newInputKey, {
        description: '',
        label: `Video ${order + 1}`,
        format: 'uri',
        id: crypto.randomUUID(),
        order,
        required: false,
        type: HandleType.Video,
      }),
    );

    return { key: newInputKey, undoEntry };
  }
}
